/**
 * Shared utilities for quiz question generators.
 *
 * Every quiz lib (`<topic>Quiz.ts`) was duplicating `pickFrom` and
 * `shuffle` helpers. Centralizing them here keeps the data layers
 * focused on quiz-specific logic.
 *
 * All helpers accept an optional RNG so tests can pass a seeded source
 * for deterministic verification. Production callers pass nothing and
 * inherit Math.random.
 */

export function pickFrom<T>(arr: readonly T[], rand: () => number = Math.random): T {
  if (arr.length === 0) {
    throw new Error("pickFrom: cannot pick from empty array");
  }
  return arr[Math.floor(rand() * arr.length)]!;
}

/** Pick `n` distinct elements from `arr`. Throws if `n > arr.length`. */
export function pickN<T>(
  arr: readonly T[],
  n: number,
  rand: () => number = Math.random,
): T[] {
  if (n > arr.length) {
    throw new Error(`pickN: cannot pick ${n} from array of length ${arr.length}`);
  }
  const pool = [...arr];
  const out: T[] = [];
  for (let i = 0; i < n; i++) {
    const idx = Math.floor(rand() * pool.length);
    out.push(pool[idx]!);
    pool.splice(idx, 1);
  }
  return out;
}

/** Fisher-Yates shuffle, returns a new array. */
export function shuffle<T>(arr: readonly T[], rand: () => number = Math.random): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

/** Per-session stats tracked by every quiz route. */
export interface SessionStats {
  attempts: number;
  correct: number;
  /** Current consecutive-correct streak. Resets on a miss. */
  streak: number;
  /** Best streak this session (since route mount). */
  bestStreak: number;
}

export const INITIAL_SESSION_STATS: SessionStats = {
  attempts: 0,
  correct: 0,
  streak: 0,
  bestStreak: 0,
};

/** Compute the next stats given a correctness outcome. Pure function so
 *  it's easy to test the streak/best-streak edge cases in isolation. */
export function advanceStats(prev: SessionStats, isCorrect: boolean): SessionStats {
  const streak = isCorrect ? prev.streak + 1 : 0;
  return {
    attempts: prev.attempts + 1,
    correct: prev.correct + (isCorrect ? 1 : 0),
    streak,
    bestStreak: Math.max(prev.bestStreak, streak),
  };
}

/** Accuracy percent, with safe handling of zero attempts (returns 0). */
export function accuracyPct(stats: SessionStats): number {
  if (stats.attempts === 0) return 0;
  return Math.round((stats.correct / stats.attempts) * 100);
}
